import type { Translation } from '@/types/translation';

// Swedish (SV) translation file.

const svTranslation: Translation = {
  common: {
    save: 'Spara',
    close: 'Stäng',
    search: 'Sök',
    back: 'Tillbaka',
    continue: 'Fortsätt',
    cancel: 'Avbryt',
    submit: 'Skicka',
    confirm: 'Bekräfta',
    delete: 'Ta bort',
    save_changes: 'Spara ändringar',
    loading: 'Läser in...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Instrumentpanel',
        fleet_overview: 'Flottöversikt',
        unit_overview: 'Alla enheter',
        campaigns: 'Kampanjer',
        members: 'Medlemmar',
      },
      dashboard: {
        active_fault_codes: {
          title: 'Aktiva felkoder',
          units_title: 'Enheter med aktiva felkoder',
        },
        connectivity: {
          title: 'Anslutning',
          units_title: 'Anslutningsstatus',
        },
        fuel_consumption: {
          title: 'Bränsleförbrukning',
          details_title: 'Detaljer om bränsleförbrukning',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Fästa flottor',
        other_fleets: 'Andra flottor',
        pin: 'Fäst flotta',
        unpin: 'Avfäst flotta',
        edit: 'Redigera flotta',
        remove: 'Ta bort flotta',
      },
      unit_overview: {
        title: 'Enheter',
        unverified_ownership: 'Overifierat ägarskap',
        add_to_fleet: 'Lägg till i flottan',
      },
      campaigns: {
        active_table: {
          title: 'Aktiva kampanjer',
          download_campaigns: 'Ladda ner kampanjer (xlsx)',
          empty_state: {
            title: 'Du har inga aktiva kampanjer för denna enhet',
          },
        },
        performed_table: {
          title: 'Utförda kampanjer',
          empty_state: {
            title: 'Du har inga utförda kampanjer tillgängliga',
          },
        },
      },
      members: {
        title: 'Medlemmar',
        remove_members_from_account: 'Ta bort {{selectedRows}} medlemmar från konto',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Instrumentpanel',
        unit_overview: 'Alla enheter',
        campaigns: 'Kampanjer',
      },
      campaigns: {
        title: 'Aktiva kampanjer',
        download_campaigns: 'Ladda ner kampanjer (xlsx)',
        empty_state: {
          title: 'Du har inga aktiva kampanjer för denna flotta',
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Specifikationer',
        campaigns: 'Kampanjer',
        coverage: 'Garanti & Skydd',
        maintenance_history: 'Registrerad serviceåtgärder',
        manuals_handbooks: 'Manualer & Handböcker',
        diagnostics_log: 'Diagnostiklogg',
        services: 'Digitala tjänster',
        settings: 'Inställningar',
      },
      spare_parts: 'Reservdelar',
      manuals_selection_description: 'Vilken produkt vill du hitta manualer och handböcker för.',
      specifications: {
        title: 'Specifikationer',
      },
      campaigns: {
        active_table: {
          title: 'Aktiva kampanjer',
          empty_state: {
            title: 'Du har inga aktiva kampanjer för denna enhet',
          },
        },
        performed_table: {
          title: 'Utförda kampanjer',
          empty_state: {
            title: 'Du har inga utförda kampanjer för denna enhet',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Information om garanti & skydd',
          message: 'För frågor om garanti & skydd, kontakta din Volvo Penta- eller OEM-återförsäljare.',
          find_dealer: 'Hitta närmaste återförsäljare',
          learn_more: 'Läs mer',
        },
        empty_state: {
          title: 'Ingen garanti och/eller skydd hittades för denna enhet',
        },
      },
      publications: {
        select_menu: {
          category: 'Kategori',
          designation: 'Beteckning',
          product_type: 'Produkttyp',
          language: 'Språk',
        },
        empty_state: {
          title: 'Inga publikationer tillgängliga för denna enhet',
        },
      },
      services: {
        title: 'Tillgängliga tjänster',
        all_services: {
          title: 'Aktivera alla tjänster',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Få alla fördelar med Core plus automatiska e-postaviseringar som skickas direkt till din valda återförsäljare.',
              checklist: [
                'Larma din återförsäljare direkt om systemet upptäcker en kritisk felkod',
                'Ge din återförsäljare möjlighet att förbereda omedelbara korrigerande åtgärder',
              ],
            },
            Industrial: {
              title: 'Aktiv övervakning',
              description:
                'Active Monitoring hjälper kunder att hålla sina Volvo Penta-produkter i drift genom att upptäcka problem tidigt, möjliggöra snabbare service och ge tydliga insikter i produktens status och användning.\n\nActive Monitoring möjliggör även Remote Software Updates, en tjänst för att installera programvarukampanjer over-the-air när det passar dig.',
              checklist: [
                'Högre drifttid tack vare tidiga varningar och proaktiv support',
                'Snabbare och bättre förberedd service med rätt information tillgänglig i förväg',
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Dela grundläggande fordonsdata med din återförsäljare för att få snabbare support vid underhåll eller oväntade problem.',
              checklist: [
                'Låt din återförsäljare övervaka produktens status och följa upp drifttimmar',
                'Låt din återförsäljare analysera felkoder direkt för proaktiv serviceplanering',
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'Core gör det möjligt för Volvo Penta-återförsäljaren att övervaka Volvo Penta-produkten. <ActiveMonitoringCore>För ytterligare information, se servicebeskrivningen för Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                'Ge din återförsäljare möjlighet att följa fordonsdata och få omedelbara aviseringar om kritiska felkoder för att förbereda omedelbara korrigerande åtgärder.',
              checklist: [
                'Larma din återförsäljare direkt om systemet upptäcker en kritisk felkod',
                'Ge din återförsäljare möjlighet att förbereda omedelbara korrigerande åtgärder',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                'Ge din återförsäljare möjlighet att följa fordonsdata och få omedelbara aviseringar om kritiska felkoder för att förbereda omedelbara korrigerande åtgärder.',
              checklist: [
                'Larma din återförsäljare direkt om systemet upptäcker en kritisk felkod',
                'Ge din återförsäljare möjlighet att förbereda omedelbara korrigerande åtgärder',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Platstjänster',
              description:
                'Location Services hjälper kunder att snabbt lokalisera och hantera sina uppkopplade Volvo Penta-produkter via en kartvy i Volvo Penta Connect, vilket gör servicehantering och flottöversikt enklare.',
              checklist: [
                'Bättre flottöversikt med en karta som visar uppkopplade enheters senaste position',
                'Snabbare serviceåtgärder genom enkel åtkomst till enhetens position, inklusive drifttimmar och felkoder',
              ],
            },
            Industrial: {
              title: 'Platstjänster',
              description:
                'Location Services hjälper kunder att snabbt lokalisera och hantera sina uppkopplade Volvo Penta-produkter via en kartvy i Volvo Penta Connect, vilket gör servicehantering och flottöversikt enklare.',
              checklist: [
                'Bättre flottöversikt med en karta som visar uppkopplade enheters senaste position',
                'Snabbare serviceåtgärder genom enkel åtkomst till enhetens position, inklusive drifttimmar och felkoder',
              ],
            },
          },
        },
        request_sent: 'Förfrågan skickad',
        empty_state: {
          title: 'Tjänsteetablering är inte tillgänglig',
          message: 'Digital tjänsteetablering är inte tillgänglig för den här enheten.',
        },
        active_services_title: 'Aktiva tjänster',
        available_services_title: 'Tillgängliga tjänster',
        activate_button: 'Aktivera tjänst',
        terminate_button: 'Avsluta',
        read_more_button: 'Läs mer',
        requires_prefix: 'Kräver {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: 'Enhetsinställningar',
          unit_name: 'Enhetsnamn',
          home_port: 'Hamn',
          operating_location: 'Driftsplats',
          upload_image: 'Ladda upp bild',
          add_files: 'Lägg till filer',
          helperText: 'Png, .jpg fil på max 3mb',
        },
        network_settings: {
          title: 'Nätverksinställningar',
          empty_state: {
            title: 'Inga aktiva nätverk tillgängliga',
            message: 'Nätverksinställningarna för denna enhet kommer att visas här när de har konfigurerats.',
          },
          updating_title: 'Uppdaterar nätverksinställningar',
          updating_description: 'Vänta medan vi hämtar den senaste nätverkskonfigurationen.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Serviceprotokoll för chassi {{chassisNumber}}',
        download_pdf: 'Ladda ner PDF',
        service: 'Service {{type}}',
        default_service: 'Drivlina {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'Allmän servicekommentar',
        no_comment: 'Inga allmänna kommentarer för detta serviceprotokoll.',
        periodic_tag: 'Varje {{months}} månad / {{engineHours}} h',
        non_periodic_month_tag: 'Vid {{months}} månad',
        non_periodic_hours_tag: 'Vid {{engineHours}} h',
        empty_state: {
          title: 'Ingen underhållshistorik tillgänglig',
          message:
            'Serviceprotokoll för denna motor kommer att visas här när de har registrerats av en Volvo Penta-återförsäljare.',
        },
        unverified_ownership_notice:
          'Viss information kan vara begränsad eftersom äganderätten till denna enhet ännu inte har verifierats. Kontakta din auktoriserade Volvo Penta-återförsäljare för att slutföra verifieringen.',
      },
    },
    settings: {
      application_settings: 'Applikationsinställningar',
      account_name: 'Flottkonto namn',
      notifications: 'Notifikationer',
      email: 'E-post',
      email_notifications: 'Aktivera e-postnotifikationer',
      sms: 'SMS',
      sms_notifications: 'Aktivera SMS-notifikationer',
    },
    support: {
      title: 'Vanliga frågor',
      description: 'Hitta kontaktinformation samt allmän information om Volvo Penta Connect.',
      professional_support: {
        tag: 'Professionellt stöd',
        title: 'De ultimata experterna - till din tjänst',
        description:
          'Kontakta en auktoriserad Volvo Penta-återförsäljare som första kontaktpunkt när du behöver hjälp med motorservice, reparation, delar eller annan support - för fritids- och yrkesfartyg.',
      },
      emergency_support: {
        tag: 'Nödstöd',
        title: 'Volvo Penta Action Service',
        description:
          'En motorstörning inträffar sällan, men om det gör det, finns Action Service där för att sätta dig i kontakt med den närmaste Volvo Penta-partnern. Vi erbjuder support på flera språk, dygnet runt, året om.',
      },
      platform_support: {
        tag: 'Plattformsstöd',
        title: 'Volvo Penta Connect Support',
        description:
          'Kontakta vårt dedikerade team för hjälp med ditt Volvo Penta Connect-konto och funktioner. Vi finns här för att hjälpa dig hantera dina motordata och anslutning på ett enkelt sätt.',
      },
      getting_started: {
        title: 'Kom igång',
        question_1: {
          title: 'Vem är Volvo Penta Connect för?',
          body: 'Volvo Penta Connect är en kundportal som ger dig en översikt över dina enheter och tillgång till relevant information såsom manualer och handböcker, kampanjer, garantistatus och mer.',
        },
        question_2: {
          title: 'Vad behöver jag göra först för att använda denna produkt?',
          body: 'Du behöver registrera en enhet på ditt konto. När en enhet har lagts till kan du övervaka enhetsdata, installera över-luften programuppdateringar och få tillgång till manualer och handböcker kopplade till den enheten.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Lägg till enhet' },
        },
        question_3: {
          title: 'Kan jag använda denna produkt utan att lägga till en enhet?',
          body: 'Nej. Volvo Penta Connect är byggt kring dina enheter. Manualer, motordata och digitala tjänster är alla kopplade till en specifik enhet. Om du inte äger eller planerar att registrera en enhet, kommer denna produkt inte att uppfylla dina behov.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Lägg till enhet' },
        },
        question_4: {
          title: 'Hur registrerar jag min motor?',
          body: 'Gå till enhetslistan och välj Lägg till enhet. Sök efter serienumret och fyll sedan i och skicka registreringsformuläret. Vissa nyare industriella motorer levereras med en förinstallerad QR-kod på motorn. Om tillgängligt kan du skanna denna kod för att starta registreringsprocessen.',
        },
        question_5: {
          title: 'Vilken information behöver jag lämna?',
          body: 'Börja med att registrera din enhet i Volvo Penta Connect. Gå till enhetslistan och välj Lägg till enhet, sök sedan efter enhetens serienummer och fyll i registreringsformuläret.<br /><br />När enheten är registrerad får du tillgång till information såsom manualer, reservdelar, kampanjer, garantistatus och digitala tjänster kopplade till den enheten.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Lägg till enhet' },
        },
        question_6: {
          title: 'Vad kan jag göra när min enhet är registrerad?',
          body: 'Med en registrerad enhet kan du:<br /><br /><ul><li>- Gruppera enheter i flottor efter fordonstyp eller användningsfall</li><li>- Övervaka enhetens status</li><li>- Installera över-luften programuppdateringar</li><li>- Få tillgång till manualer och handböcker specifika för din enhet</li></ul>',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Enhetsöversikt' },
        },
        question_7: {
          title: 'Var kan jag hitta manualer och handböcker?',
          body: 'Manualer och handböcker blir tillgängliga när en enhet har lagts till i ditt konto, så att vi kan visa dokumentation som matchar din specifika enhet.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Lägg till enhet' },
        },
        question_8: {
          title: 'Hur lägger jag till en användare i mitt flottkonto?',
          body: 'För att lägga till en ny användare måste du bjuda in dem från sidan Medlemmar.<br /><br /><ul><li>1. Gå till Medlemmar</li><li>2. Välj Lägg till medlem</li><li>3. Skapa en inbjudningslänk och kopiera den</li><li>4. Dela länken med personen du vill bjuda in</li><li>5. När inbjudan har accepterats, tilldela en roll till den nya medlemmen</li></ul><br/>Endast användare med behörighet att hantera medlemmar kan skicka inbjudningar och tilldela roller.',
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Medlemmar' },
        },
        question_9: {
          title: "Varför märks min enhet som 'Overifierad ägare'?",
          body: 'Nyregistrerade enheter markeras tillfälligt som Overifierad ägare tills ägarskapet har bekräftats.<br /><br />Ägarskapet kan verifieras av en auktoriserad Volvo Penta-återförsäljare eller av vårt supportteam. När det är verifierat tas märket bort automatiskt.<br /><br />Om du behöver hjälp med verifieringen, kontakta din auktoriserade Volvo Penta-återförsäljare.',
        },
        question_10: {
          title: 'Varför kan jag inte se mina enheter?',
          body: 'Dina enheter kanske inte visas i ditt konto av flera skäl. Kontrollera dessa vanliga scenarier:<br /><br />E-postmatchning:<br/>Dina enheter är kopplade till en specifik e-postadress. Om du skapade ett konto med en annan adress kommer enheterna inte att vara synliga. Du kan skapa ett nytt konto med rätt e-postadress eller kontakta en auktoriserad Volvo Penta-återförsäljare för att hjälpa till att registrera om din enhet.<br /><br />Väntande registrering:<br/>Om du har lagt till en ny industrienhet kanske den inte finns i våra register än. Dessa enheter visas inte i Volvo Penta Connect förrän vårt supportteam har slutfört den manuella registreringsprocessen.<br /><br />Kontoroller:<br/>Din åtkomst till specifika enheter beror på din tilldelade roll. Om du tror att din åtkomst är begränsad, kontakta din flottchef för att säkerställa att du har rätt kontoroll.',
        },
      },
      connectivity_telematics: {
        title: 'Connectivity & telematics',
        question_1: {
          title: 'Hur vet jag om min enhet är ansluten?',
          body: 'Du kan se en enhets anslutningsstatus bredvid dess namn i enhetslistan och på enhetens sida.<br /><br /><GreenDot /> – Enheten är online. Data har mottagits inom de senaste 2 timmarna.<br /><br /><GreyDot /> – Enheten är offline. Ingen data har mottagits under de senaste 2 timmarna.<br /><br /><YellowDot /> – Anslutning är tillgänglig men inte helt konfigurerad.<br /><br />Ingen punkt – Enheten har ingen anslutningskapacitet. Den kan vara berättigad till installation av en telematik-gateway (TGW) via en återförsäljare.<br /><br />Om din enhet är offline kan den fortfarande användas som vanligt, men datauppdateringar återupptas när enheten återansluts.',
        },
        question_2: {
          title: 'Varför visas ingen anslutningsstatus för min enhet?',
          body: 'Enheter utan statusindikator har ingen telematik-gateway (TGW) installerad eller aktiverad. Dessa enheter kan anslutas genom att installera en TGW via en Volvo Penta-återförsäljare.',
        },
        question_3: {
          title: 'Vad är en telematik-gateway (TGW)?',
          body: 'En telematik-gateway (TGW) är en hårdvaruenhet som installeras på enheten och möjliggör anslutning och dataöverföring till Volvo Penta Connect.<br /><br />Utan en TGW kan enheten inte övervakas digitalt.',
        },
        question_4: {
          title: 'Hur ansluter jag min enhet till Volvo Penta Connect?',
          body: 'För att ansluta en enhet måste följande steg slutföras:<br /><br /><ul><li>- Enheten måste ha en TGW installerad</li><li>- TGW måste aktiveras av en återförsäljare eller OEM</li><li>- Nödvändiga digitala tjänster måste aktiveras</li><li>- Ägaren måste godkänna databehandlingsavtalet</li></ul><br /><br />När detta är klart kommer enheten att visas med anslutningsstatus i Volvo Penta Connect.',
        },
        question_5: {
          title: 'Vilka smartphones/surfplattor är kompatibla med Volvo Penta Connect-appen?',
          body: 'För iOS-enheter måste operativsystemversionen vara iOS 17 eller senare. För Android-enheter måste operativsystemversionen vara 9.0 eller senare.',
        },
        question_6: {
          title: 'Vem kan hjälpa mig att installera eller aktivera anslutning?',
          body: 'En Volvo Penta-återförsäljare kan hjälpa till med installation av TGW, aktivering och serviceinställningar.',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Hitta en Volvo Penta-återförsäljare',
          },
        },
        question_7: {
          title: 'Hur konfigurerar jag Wi-Fi för min enhet?',
          body: 'Du kan konfigurera din Wi-Fi-anslutning direkt i applikationen. Följ dessa steg för att komma igång:<br /><br /><ul><li>- **Navigera till inställningar:** Gå till Wi-Fi-konfigurationssektionen i din instrumentpanel.</li><li>- **Följ guiden:** Använd instruktionsboken (PDF) för steg-för-steg-anvisningar för att slutföra din konfiguration.</li><li>- **Anslut din enhet:** När konfigurationen är klar kommer din enhet automatiskt att hålla sig ansluten till din digitala arbetsyta.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: 'Instruktionsmanual',
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Kampanjer & programuppdateringar',
        question_1: {
          title: 'Vad är en kampanj?',
          body: 'En kampanj är en åtgärd som initieras av Volvo Penta för att säkerställa produktkvalitet. Om en kampanj påverkar en av dina registrerade enheter kommer du att meddelas och guidas genom de nödvändiga stegen.',
          link: {
            href: 'https://www.volvopenta.com/connect/campaigns',
            label: 'Översikt över kampanjer',
          },
        },
        question_2: {
          title: 'Vad behöver jag göra när jag har en aktiv kampanj?',
          body: 'Det beror på kampanjtypen:<br /><br /><ul><li>- **Återkallelse / Kvalitet / Service:** Kontakta din lokala Volvo Penta-återförsäljare</li><li>- **Installation / OTA:** Dessa kampanjer kan slutföras över luften med hjälp av Volvo Penta-appen. Ladda ner appen och följ instruktionerna i appen.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Hitta din återförsäljare',
          },
          ios: { href: '#', label: 'App store' },
          android: { href: '#', label: 'Google play' },
        },
        question_3: {
          title: 'Vad behöver jag innan jag installerar en överluftskampanj (OTA)?',
          body: 'Innan du påbörjar en OTA-installation, se till att:<br /><br /><ul><li>- Du har valt rätt enhet</li><li>- Bluetooth är aktiverat på din enhet</li><li>- Du är inom 3 meter från enheten</li><li>- Alla nödvändiga förutsättningar som visas i produkten är uppfyllda</li><li>- Enheten förblir påslagen och ostörd under installationen</li></ul><br/>Om något krav inte uppfylls, startar inte installationen.',
        },
        question_4: {
          title: 'Jag har problem med att installera en överluftskampanj (OTA).',
          body: 'Om installationen misslyckas eller inte startar:<br /><br /><ul><li>- Kontrollera att Bluetooth är aktiverat och att du är nära enheten</li><li>- Håll dig inom 3 meter under hela installationen</li><li>- Starta om appen och försök igen</li><li>- Kontrollera att alla förutsättningar för enheten är uppfyllda</li></ul><br/>Om problemet kvarstår, kontakta support.',
        },
        question_5: {
          title: 'Var kan jag se mina slutförda överluftskampanjer (OTA)?',
          body: 'Du kan se slutförda OTA-installationer på enhetens sida. Öppna enheten och gå till fliken Kampanjer för att se hela installationshistoriken.',
        },
        question_6: {
          title: 'Kan dålig anslutning påverka en OTA-installation?',
          body: 'Ja. En OTA-installation kan misslyckas om enheten har instabil eller ingen mobil- eller Wi-Fi-anslutning. Se till att enheten har en pålitlig anslutning och förblir ansluten tills installationen är klar.',
        },
        question_7: {
          title: 'Varför behöver min enhet vara regelbundet ansluten?',
          body: 'Regelbunden anslutning hjälper till att säkerställa att din enhet är redo för programuppdateringar. Om en enhet inte har varit ansluten under en tid kan nödvändiga data eller förberedelser saknas, vilket kan försena eller blockera en OTA-installation.',
        },
        question_8: {
          title: 'Vad händer om jag inte slutför en kampanj i tid?',
          body: 'Kampanjer installeras inte automatiskt. Om en kampanj inte slutförs inom den angivna tidsramen kan det inte längre vara möjligt att utföra den. Följ alltid instruktionerna som visas och slutför kampanjen inom den angivna perioden.',
        },
      },
      maintenance_service: {
        title: 'Underhåll & service',
        question_1: {
          title: 'Kan jag se min enhets underhållshistorik?',
          body: 'Ja. Varje enhet har en flik för underhållshistorik där slutförda tjänster listas. Endast tjänster utförda av auktoriserade Volvo Penta-återförsäljare visas. Om en tjänst saknas, kontakta din återförsäljare direkt.<br /><br />Välj en enhet och öppna fliken för underhållshistorik för att se detaljerna.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Enhetsöversikt' },
        },
        question_2: {
          title: 'Varför kan jag inte se vilken återförsäljare som utförde en tjänst?',
          body: 'Uppgifter om återförsäljare visas endast för verifierade ägare av enheten.<br /><br />För att se återförsäljarens namn och plats för en slutförd tjänst måste du vara registrerad som ägare. Om du ännu inte är verifierad, kontakta din auktoriserade Volvo Penta-återförsäljare för att bekräfta ditt ägarskap.',
        },
      },
      warranty_coverage: {
        title: 'Garanti & utökad täckning',
        question_1: {
          title: 'Hur vet jag om mina enheter har någon garanti & täckning?',
          body: 'Varje enhet har en flik för garanti & täckning som visar eventuella aktiva garantier. Välj en enhet och öppna fliken för att se detaljer.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Enhetsöversikt' },
        },
        question_2: {
          title: 'Vad är standardgaranti?',
          body: 'Alla Volvo Penta-produkter inkluderar en fabriksstandardgaranti. Täckningen gäller under en definierad tidsperiod eller antal driftstimmar—det som inträffar först—och beror på produkten och dess användning.',
        },
        question_3: {
          title: 'Vad är standardgaranti utan timbegränsning?',
          body: 'Vissa installationstyper har ingen gräns för driftstimmar under det första och/eller andra året av garantitäckning. Detaljer beror på produkten och installationen och definieras i garantivillkoren.',
        },
        question_4: {
          title: 'Vad är komponentgaranti?',
          body: 'Komponentgaranti täcker specifika större komponenter i din Volvo Penta-produkt. Täckningens varaktighet och driftstimmar varierar beroende på komponent och definieras i garantivillkoren.',
        },
        question_5: {
          title: 'Vad är utsläppsgaranti?',
          body: 'Motorer som är godkända enligt US EPA- eller California ARB-föreskrifter omfattas av utsläppsgaranti. Detta gäller under en definierad tidsperiod eller antal driftstimmar, enligt utsläppsgarantivillkoren.',
        },
      },
      notification: {
        title: 'Meddelanden',
        question_1: {
          title: 'Varför får jag inga meddelanden om mina enheter?',
          body: 'Kontrollera dina meddelandeinställningar. Se till att meddelanden är aktiverade på sidan Inställningar.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Inställningar' },
        },
      },
    },
    login: {
      hero_title: 'Välkommen till Volvo Penta Connect',
      hero_body:
        'Allt om din Volvo Penta-motor eller produkt på ett och samma ställe. Manualer, handböcker, reservdelar, återkallelser, kvalitetskampanjer, garantistatus med mera.',
      log_in: 'Logga in',
      blurb_back_button: 'Hem',
      blurb_bolded_main_text:
        'Välkommen till Volvo Penta Connect, den nya plattformen för flottägare och fritidsbåtsägare.',
      blurb_regular_main_text:
        'Registrera din motor för att få tillgång till relevant information som manualer, återkallelser och garantistatus. Registreringen säkerställer att din lokala återförsäljare är redo att stödja dig med rätt delar och tjänster. Bäst av allt är att det är helt gratis.',
      CTAs: {
        manuals_label: 'Bekväm åtkomst till manualer',
        manuals_title: 'Alla dina manualer med ett klick',
        manuals_body:
          'Med Volvo Penta Connect behöver du inte längre söka efter serienumret för att få tillgång till manualer. Vår plattform erbjuder en lösning med ett klick, som ger dig hela samlingen av manualer och handböcker för alla dina motorer och produkter.',
        efficiency_label: 'Effektivitet',
        efficiency_title: 'Hitta snabbt rätt reservdelar',
        efficiency_body:
          'Volvo Penta Connect erbjuder en lättanvänd funktion som förenklar processen att hitta rätt reservdel för din Volvo Penta-motor - utan besväret att söka efter serienummer eller motormodell.',
        service_label: 'Spåra produktunderhåll',
        service_title: 'Visa den registrerade servicehistoriken för dina produkter',
        service_body:
          'Håll dig uppdaterad om hälsotillståndet för dina produkter med Volvo Penta Connect. Få en omfattande översikt, inklusive servicedetaljer, av din registrerade servicehistorik för alla dina Penta-produkter.',
      },
      cards: {
        uptime_label: 'Säkerställ drifttid',
        uptime_title: 'Upptäck om du har aktiva kampanjer',
        uptime_body:
          'Med Volvo Penta Connect har det aldrig varit enklare att hålla koll på dina produktkampanjer. Se till att dina produkter är uppdaterade och alltid fungerar som de ska.',
        connect_label: 'Håll koll på alla dina garantier',
        connect_title: 'Dina garantier i Volvo Penta Connect',
        connect_body:
          'Få en översikt över alla dina garantier och täckningar. Kontrollera om de är aktiva och hur länge de kommer att vara giltiga, och få notiser när de är på väg att gå ut.',
      },
    },
    welcome: {
      title: 'Välkommen!',
      body: 'För att fullt ut kunna använda Volvo Penta Connect behöver du registrera ett fartyg eller en maskin på ditt konto. När det är tillagt får du tillgång till manualer och handböcker, enklare åtkomst till relevanta reservdelar, garantistatus och mycket mer.',
      get_help: 'Få hjälp',
      get_started: 'Kom igång',
      accordion: {
        who_is_it_for: {
          title: 'Vem är Volvo Penta Connect för?',
          body: 'Volvo Penta Connect är en kundportal där du får en översikt över dina enheter och relevant information såsom manualer och handböcker, kampanjer, garantistatus med mera.',
        },
        how_to_register: {
          title: 'Kan jag använda denna produkt utan att registrera en enhet?',
          body: 'Du behöver registrera en enhet på ditt konto. När en enhet är tillagd kan du övervaka enhetsdata, installera programuppdateringar över luften och få tillgång till manualer och handböcker kopplade till den enheten.',
        },
        can_i_use_without_registering: {
          title: 'Var kan jag hitta manualer och handböcker?',
          body: 'Manualer och handböcker blir tillgängliga när en enhet har lagts till på ditt konto, så att vi kan visa dokumentation som matchar din specifika enhet.',
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Välkommen till {{name}}!',
        invite_description:
          'Du har blivit inbjuden till arbetsytan {{fleetName}}. Här kan du övervaka flottans och enheternas hälsa, följa analyser samt få notiser om uppdateringar av kampanjer och garantier.',
        accept: 'Acceptera inbjudan',
        dismiss: 'Avvisa',
      },
      add_units_fleet_modal: {
        button_add: 'Skapa flotta',
        button_update: 'Uppdatera flotta',
        title_add: 'Skapa flotta',
        title_update: 'Uppdatera flotta',
        title_success: 'Flotta skapad',
        text_success: 'Din flotta har skapats. Om den nya flottan inte visas i listan, uppdatera sidan.',
        fleet_name: 'Ange flottans namn eller välj din flotta',
        select_your_fleet: 'Välj din flotta',
        click_to_create_fleet: 'Klicka för att skapa flotta',
        table: {
          unit_name: 'Enhetsnamn',
          location: 'Plats',
          engines: 'Motorer',
          fleets: 'Flottor',
        },
        cancel: 'Hoppa över för nu',
        submit: {
          add_units: 'Lägg till enhet(er) i flotta',
          create_fleet: 'Skapa flotta',
          close: 'Stäng',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Ta bort från flotta',
        title_remove: 'Ta bort enheter från flotta',
        description: 'Du är på väg att ta bort de valda enheterna från flottan. Denna åtgärd kan inte ångras.',
        confirmation: 'Är du säker på att du vill ta bort de valda enheterna från flottan?',
        cancel: 'Avbryt',
        submit: {
          create_fleet: 'Ta bort från flotta',
        },
      },
      delete_fleet_modal: {
        title: 'Ta bort flotta',
        description:
          'Att ta bort en flotta från ditt konto är permanent. Du kan när som helst lägga till flottan igen.',
        confirmation: 'Är du säker på att du vill ta bort följande flotta?',
        submit: 'Ja, ta bort flottan från kontot',
      },
      edit_fleet_modal: {
        title: 'Redigera flotta',
        fleet_name: 'Flottans namn',
        select_icon: 'Välj ikon',
      },
      add_new_unit_modal: {
        button: 'Lägg till enhet',
        search_unit: {
          title: 'Hitta din enhet',
          description: 'Sök efter enheten du vill lägga till med motorns serienummer.',
          serial_number: 'Motorns serienummer',
          find_serial_number: 'Hur du hittar ditt serienummer',
          is_not_found: {
            label: 'Serienumret finns inte i våra register',
            body: 'Se till att du har hittat rätt serienummer. Om det fortfarande inte fungerar, kontakta din Volvo Penta-återförsäljare eller hitta en i vår <a>Återförsäljarlokalisator</a> för att få hjälp.',
          },
          is_not_registered_industrial: {
            label: 'Enheten är inte registrerad för denna produkt',
            body: 'Registrera enheten för att lägga till den i ditt konto. Registreringsförfrågan hanteras av vårt supportteam och enheten läggs till när den är slutförd.',
          },
          is_not_registered_marine: {
            label: 'Enheten är inte registrerad för denna produkt',
            body: 'Enheten måste registreras för att du ska kunna lägga till den i ditt konto. Kontakta din Volvo Penta-återförsäljare eller hitta en i vår <a>Återförsäljarlokalisator</a> för hjälp med enhetsregistrering.',
          },
          is_maximum_units_reached: {
            label: 'Maximalt antal enheter uppnått',
            body: 'Du kan lägga till upp till tre enheter som inte ägs av dig. För att lägga till fler måste du vara registrerad som ägare av enheten. Kontakta din Volvo Penta-återförsäljare för hjälp, eller använd vår <a>Återförsäljarlokalisator</a> för att hitta en nära dig.',
          },
          is_unit_already_added: {
            label: 'Enheten är redan tillagd i ditt konto',
            body: 'Denna enhet finns redan i ditt konto och behöver inte registreras igen. Du kan hitta den i din enhetslista.',
          },
        },
        unit_information: {
          title: 'Ange enhetsinformation',
          description:
            'Fyll i uppgifterna nedan för att lägga till din enhet i Volvo Penta Connect och få relevant information om din Penta-produkt.',
          privacy_information:
            'Volvo Penta registrerar och använder vissa personuppgifter om dig. Om du vill veta mer om hur Volvo Penta behandlar dina personuppgifter, vänligen gå till <a>Integritetsinformation för registrering av ägare eller operatör</a>.',
        },
        marine_unit_information: {
          serial_number: 'Motorns serienummer',
          serial_number_helperText: 'Volvo Penta-serienumret finns på motorplåten.',
          vessel_name: 'Båtnamn',
          vessel_name_helperText: 'Namnet (t.ex. Black Pearl) på din båt.',
          vessel_id: 'Båt-ID',
          imo_number: 'IMO-nummer',
          imo_number_helperText: 'Internationellt sjöfartsorganisationsnummer som unikt identifierar din båt.',
          mmsi_number: 'MMSI-nummer',
          mmsi_number_helperText: 'Maritime Mobile Service Identity-nummer som används för radiokommunikation.',
          manufacturer: 'Tillverkare',
          manufacturer_helperText: 'Tillverkaren av din båt.',
          model: 'Modell',
          model_helperText: 'Modellen på båten.',
          vessel_segment: 'Båtsegment',
          type_of_vessel: 'Båttyp',
          home_port: 'Hemmahamn',
          home_port_helperText: 'Plats eller hemmahamn för din båt',
          add_unit: 'Lägg till enhet',
        },
        marine_unit_added: {
          title: 'Din enhet har lagts till',
          description:
            'Din båt är tillagd och du kommer att kunna se din båts information, ladda ner manualer, hitta reservdelar och mer.',
          add_another_unit: 'Lägg till en annan enhet',
          go_to_unit: 'Gå till enhet',
        },
        industrial_contact_information: {
          title: 'Ange kontaktinformation',
          description:
            'Fyll i ägarens uppgifter nedan för att lägga till din enhet. Dina angivna uppgifter kommer att kontrolleras manuellt av vårt supportteam innan du registreras som ägare.',
          proof_of_ownership: 'Ägandebevis',
          proof_of_ownership_helperText:
            'Ett ägandebevis (t.ex. kontrakt, kvitto eller orderbekräftelse) krävs för att registrera företaget som ägare av maskinen. Om du inte kan tillhandahålla ägandebevis, kontakta din återförsäljare.',
          proof_of_ownership_upload:
            'Dra och släpp eller klicka för att bläddra bland filer, PDF, png, jpg eller heic-filer på max 3 MB vardera.',
          proof_of_ownership_qr: 'Äganderätt bekräftad via QR-kod',
          contact_person: 'Kontaktperson',
          contact_person_description: 'Du kommer att registreras som kontaktperson för företaget och denna maskin.',
          first_name: 'Förnamn',
          last_name: 'Efternamn',
          email: 'E-post',
          company_details: 'Företagsuppgifter',
          company_details_description: 'Ange informationen om företaget som äger maskinen.',
          company_name: 'Företagsnamn',
          company_name_helperText: 'Företaget som äger maskinen.',
          vat_number: 'Momsregistreringsnummer',
          address_line: 'Adress',
          zip_code: 'Postnummer',
          city: 'Stad',
          state: 'Delstat / Provins',
          country: 'Land',
          notification: 'Meddelandeinställningar',
        },
        industrial_unit_information: {
          engine_serial_number: 'Motorns serienummer',
          engine_serial_number_helperText:
            'Om din maskin har mer än en motor, klicka för att lägga till ett annat fält och ange den ytterligare motorns serienummer.',
          add_engine: 'Lägg till motor',
          machine_name: 'Maskinnamn',
          machine_name_helperText: 'Namnet (t.ex. T4, Enhet 11) på din maskin, fordon eller kraftgenerator.',
          machine_id: 'Maskin-ID',
          machine_id_helperText: 'Det unika identifikationsnumret som tillhandahålls av originaltillverkaren (OEM).',
          manufacturer: 'Tillverkare',
          manufacturer_helperText:
            'Tillverkaren av din maskin, fordon eller kraftgenerator (t.ex. Magni, Inmesol, Atlas).',
          model: 'Modell',
          model_helperText: 'Modellen på din maskin, fordon eller kraftgenerator.',
          machine_segment: 'Maskinsegment',
          machine_segment_helperText: 'Segmentet din maskin tillhör (t.ex. Materialhantering, gruvdrift, skogsbruk).',
          type_of_machine: 'Maskintyp',
          type_of_machine_helperText: 'Vilken typ av maskin det är (t.ex. terminaltraktor, skördare, gaffeltruck).',
          operating_location: 'Driftsplats',
          operating_location_helperText: 'Plats eller driftsplats för din maskin.',
        },
        industrial_unit_added: {
          title: 'Enhet tillagd',
          description:
            'Din maskin är tillagd och du kommer att kunna se din maskininformation, ladda ner manualer, hitta reservdelar och mer.',
          manual_ownership_registration: 'Manuell ägarregistrering',
          manual_ownership_registration_description:
            'Registrering och ägarkontroll görs manuellt av vårt supportteam. <b>För närvarande har vi hög efterfrågan, vilket leder till långa handläggningstider, förvänta dig mellan 2-6 veckor.</b> Du kommer att få ett bekräftelsemail när din förfrågan godkänns.',
          add_another_unit: 'Lägg till en annan enhet',
          go_to_unit: 'Gå till enhet',
        },
        industrial_no_unit_added: {
          title: 'Maskinen kommer inte att visas i Volvo Penta Connect',
          description:
            'Din maskin finns inte i våra register. Den kommer inte att visas i Volvo Penta Connect förrän den manuella registreringsprocessen av vårt supportteam är slutförd.',
          manual_registration: 'Manuell registrering',
          manual_registration_description:
            'Registrering och ägarkontroll görs manuellt av vårt supportteam. <b>För närvarande har vi hög efterfrågan, vilket leder till långa handläggningstider, förvänta dig mellan 2-6 veckor.</b> Du kommer att få ett bekräftelsemail när din förfrågan godkänns.',
          manuals_campaigns: 'Manualer, delar och kampanjer',
          manuals_campaigns_description:
            'Om du letar efter manualer och handböcker, reservdelar eller kampanjer - här är länkar där du kan få det tills registreringen är slutförd. Du har också fått denna information i din e-postinkorg.',
          add_another_unit: 'Lägg till en annan enhet',
        },
      },
      invite_members_modal: {
        button: 'Lägg till medlemmar',
        title: 'Väntande inbjudningslänkar',
        empty_state: {
          title: 'Hittade inga inbjudningar.',
          message: 'Försök att skapa och kopiera en inbjudan till ditt urklipp med knappen nedan.',
        },
        generate_invitation_link: 'Skapa och kopiera ny inbjudningslänk',
      },
      remove_members_modal: {
        title: 'Ta bort medlem(ar)',
        description:
          'Att ta bort en medlem från {{count}} kontot görs permanent. Du har möjlighet att bjuda in en medlem igen när som helst.',
        are_you_sure: 'Är du säker på att du vill ta bort följande medlem(ar)?',
        cancel: 'Avbryt',
        submit: 'Ja, ta bort medlem(ar) från kontot',
      },
      add_network_modal: {
        button: 'Lägg till nätverk',
        title: 'Lägg till nätverk',
        refresh_network: 'Uppdatera nätverk',
        edit_network: 'Redigera nätverk',
        remove_network: 'Ta bort nätverk',
        remove_network_description:
          'Ta bort nätverket från din sparade lista för detta fordon / fartyg. Borttagningen av nätverket är permanent. Du kan alltid lägga till nätverket igen.',
        network_name: 'Nätverksnamn (SSID)',
        network_name_helperText: 'Nätverks-ID för wifi-routern',
        password: 'Lösenord',
        password_helperText: 'Lösenord till wifi-routern',
        error_title: 'Något gick fel',
        error_description: 'Det gick inte att uppdatera nätverket. Försök igen senare.',
        priority: 'Prioritet',
      },
      wifi_polling_modal: {
        title: 'Updating Network Settings',
        title_error: 'Network Settings Update',
        connecting: 'Connecting to your device...',
        searching: 'Searching for your network settings...',
        almost: 'Almost there, fetching configuration...',
        hang_tight: 'Still working on it, hang tight...',
        communicating: 'Communicating with the gateway...',
        moment: 'Just a moment longer...',
        timeout: 'Unable to reach your device. Please try again later.',
        error: 'Something went wrong while updating network settings. Please try again.',
      },
      wifi_error_log_modal: {
        title: 'Fellogg',
        no_errors: 'Inga fel hittades',
        close: 'Stäng',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Aktivera tjänst',
          description:
            'För att hjälpa oss att förbereda rätt aktiveringsavtal och begära rätt kontaktuppgifter, ange för vems räkning du aktiverar den här tjänsten.',
          myself: 'Mig själv',
          myself_helper: 'Aktivera den här tjänsten för din personliga maskin eller ditt fartyg.',
          company: 'Ett företag',
          company_helper: 'Aktivera den här tjänsten på uppdrag av ett företag, en flotta eller en organisation.',
        },
        contract_details_content: {
          title: 'Kontaktuppgifter',
          first_name: 'Förnamn',
          last_name: 'Efternamn',
          email_address: 'E-postadress',
          company_name: 'Företagsnamn',
          company_details_title: 'Företagsuppgifter',
          company_details_description: 'Ange uppgifterna för företaget som äger maskinen.',
          vat_number: 'Momsregistreringsnummer',
          social_security_number: 'Personnummer',
          state_province: 'Delstat/provins',
          country: 'Land',
          address: 'Adress',
          city: 'Stad',
          postal_code: 'Postnummer',
          is_correct_info: 'Härmed bekräftar jag att informationen ovan är sann och korrekt.',
          is_terms_checked:
            'Jag erkänner och bekräftar att jag har läst och förstått villkoren för Avtalet om Digitala Tjänster (inklusive bilagor) och att jag är behörig att acceptera nämnda <a>avtal</a> med juridiskt bindande verkan.',
          data_management_agreement: 'Jag har även läst och förstått <a>databehandlingsavtalet</a>.',
          remote_software_updates_title: 'Remote Software Updates',
          service_description_for: 'Tjänstebeskrivning för {{serviceName}}',
          is_service_details_checked:
            'Härmed bekräftar jag att jag har läst och förstått <serviceDescription /> och att jag är behörig att acceptera nämnda servicedetaljer.',
          is_data_act_checked:
            'EU:s datalagsstiftning kräver att vi tillhandahåller dig med förhandsinformation som beskriver hur en Volvo Penta Connected Product (som släppts ut på marknaden i en EU-medlemsstat) genererar produktdata, hur produktdata samlas in, hur du som användare kan få tillgång till den och hur den används av Volvo Penta. Den fullständiga informationen finns tillgänglig på <a>EU Data Act | Volvo Penta</a>. Genom att markera denna ruta och fortsätta med köp, leasing eller uthyrning av Volvo Penta Connected Product, bekräftar du att du har fått tillgång till denna förhandsinformation enligt EU:s datalagsstiftning.',
        },
        choose_receiver_content: {
          title: 'Välj en återförsäljare',
          description:
            'Lägg till en återförsäljare för att ta emot varningsaviseringar. Detta säkerställer maximal drifttid och mest effektiv serviceplanering för din Volvo Penta-produkt.',
          find_nearest_dealer: 'Hitta din närmaste återförsäljare',
          dealer: 'Återförsäljare',
          dealer_placeholder: 'Ange återförsäljarens namn och/eller deras e-postadress',
        },
        thanks_content: {
          title: 'Tack!',
          description:
            'Observera att aktiveringen inte är omedelbar och kan ta lite tid. Vi meddelar dig via e-post när tjänsten är redo att användas.',
          description_all_services: 'Vi meddelar dig via e-post när tjänsterna är redo att användas.',
          Marine: {
            description:
              'Observera att aktiveringen inte är omedelbar och kan ta lite tid. Vi meddelar dig via e-post när aktiveringen är slutförd.',
          },
        },
        deactivate_service: {
          title: 'Avsluta tjänst',
          description: 'Är du säker på att du vill avsluta tjänsten?',
          description_all: 'Är du säker på att du vill avsluta alla tjänster, ({{serviceNames}})?',
          deactivate: 'Avsluta',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} objekt valda',
      page: 'Sida {{currentPage}} av {{totalPages}}',
      unit_table: {
        unit_name: 'Enhetsnamn',
        model_brand: 'Modell/märke',
        location: 'Plats',
        engines: 'Motorer',
        operating_hours: 'Drifttimmar',
        fleets: 'Flottor',
        campaigns: 'Kampanjer',
        digital_services: 'Digitala tjänster',
        unverified_ownership: 'Overifierat ägarskap',
      },
      campaigns_table: {
        campaign: 'Kampanj',
        download: 'Ladda ner',
        created_date: 'Skapad datum',
        valid_to: 'Giltig till',
        product: 'Produkt',
        type: 'Typ',
      },
      members_table: {
        name: 'Namn',
        email: 'E-post',
        account: 'Konto',
      },
      invitations_table: {
        creation_date: 'Skapelsedatum',
      },
      specifikationer_table: {
        driveline: 'Drivlina',
        product_type: 'Produkttyp',
        product: 'Produkt',
        serial_number: 'Serienummer',
        operating_hours: 'Drifttimmar',
        engine: 'Motor',
        segment: 'Segment',
        rated_power: 'Nominell effekt',
        rated_speed: 'Nominellt varvtal',
      },
      network_settings_table: {
        priority: 'Prioritet',
        ssid: 'SSID',
        frequency: 'Frekvens',
        last_updated: 'Senast uppdaterad',
        connection: 'Anslutning',
        connected: 'Ansluten',
        not_connected: 'Ej ansluten',
        lost_connection: 'Förlorad anslutning',
        disconnected: 'Frånkopplad',
      },
      coverage_table: {
        type: 'Typ',
        description: 'Garanti/Skyddsbeskrivning',
        expiration_hours: 'Utgångstimmar',
        expiration_date: 'Utgångsdatum',
      },
      fault_codes_table: {
        unit: 'Enhet',
        severity: 'Allvarlighetsgrad',
        fault_code: 'Felkod',
        fault_description: 'Felkodsbeskrivning',
        failure_event: 'Felhändelse',
        timestamp: 'Tidstämpel',
        operating_hours: 'Drifttimmar',
        occurrences: 'Förekomster',
      },
      connectivity_table: {
        unit: 'Enhet',
        operating_hours: 'Drifttimmar',
        last_seen: 'Senast sedd',
        status: 'Anslutningsstatus',
      },
      fuel_consumption_table: {
        unit: 'Enhet',
        chassis_id: 'Chassi-ID',
        operating_hours: 'Drifttimmar (h)',
        fuel_efficiency: 'Bränsleeffektivitet (l/h)',
        total_fuel: 'Total bränsle (l)',
      },
      publications_table: {
        title: 'Titel',
        description: 'Beskrivning',
        designation: 'Produkt',
        product: 'Produkttyp',
        size: 'Storlek',
        languageCode: 'Spr.',
        buy: 'Köp',
      },
      maintenance_dealer_table: {
        header: 'Återförsäljare',
        dealer_name: 'Återförsäljarnamn',
        location: 'Plats',
      },
      maintenance_protocol_details_table: {
        header: 'Serviceprotokolldetaljer',
        title: 'Titel',
        current_service: 'Nuvarande service',
        previous_service: 'Tidigare service',
        date: 'Datum',
        operating_hours: 'Drifttimmar',
        unit_age: 'Enhetsålder',
        months: 'månader',
      },
      maintenance_driveline_table: {
        header: 'Drivlina {{driveline}}',
        name: 'Namn',
        serial_number: 'Serienummer',
        product: 'Produkt',
      },
      maintenance_service_table: {
        header: 'Service {{service}}',
        activity: 'Aktivitet',
        detail: 'Detalj',
        last_performed: 'Senast utförd',
        performed: 'Utförd',
        complete: 'Slutförd',
        not_performed: 'Ej utförd',
      },
    },
    tooltip: {
      copy_clipboard: 'Kopiera till urklipp',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Hem',
      settings: 'Inställningar',
      support: 'Support',
    },
    user_menu: {
      title: 'Användarmeny',
      account_settings: 'Kontoinställningar',
      logout: 'Logga ut',
      switch_fleet_account: 'Byt flottkonto',
    },
    footer: {
      useful: 'Användbart',
      manuals_and_handbooks: 'Manualer & handböcker',
      dealer_locator: 'Återförsäljarlokalisator',
      warranty: 'Garanti',
      parts_column: 'Delar och tillbehör',
      parts: 'Delar',
      remanufactured: 'Renoverade komponenter och motorer',
      contact: 'Kontakt',
      action_service: 'Volvo Penta Action Service',
      faq: 'Vanliga frågor',
      contact_us: 'Kontakta oss',
      privacy_policy: 'Integritetspolicy',
      cookie_policy: 'Cookiepolicy',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: 'Bjud in andra till ditt konto',
        message:
          'Lägg till medlemmar för att dela åtkomst till dina enheter och flottor. Du kan bjuda in kollegor, affärspartners eller familjemedlemmar och tilldela roller för att styra vad de kan se och hantera. Kopiera länken och skicka den till din medlem.',
      },
      members_digitalaccount_cta: {
        heading: 'Bjud in andra till ditt konto',
        message:
          'Att bjuda in medlemmar kräver en verifierad enhetesägare. När ditt ägarskap har bekräftats kommer du att kunna bjuda in kollegor, affärspartners eller familjemedlemmar och tilldela roller för att styra vad de kan se och hantera.<br /><br />För att verifiera ägarskapet, vänligen kontakta din auktoriserade Volvo Penta-återförsäljare.',
      },
      action_cta: {
        heading: 'Hur man vidtar åtgärder',
        message: 'För att utföra en kampanj, kontakta din Volvo Penta- eller OEM-återförsäljare.',
        find_dealer: 'Hitta din närmaste återförsäljare',
        learn_more: 'Läs mer',
      },
      notifications_cta: {
        heading: 'Meddelanden',
        message: 'Håll dig uppdaterad och få meddelanden via e-post när enhet har en installerbar kampanj tillgänglig.',
        notification_settings: 'Meddelandesinställningar',
      },
    },
    role_types: {
      title: 'Rollinformation',
      manager: {
        title: 'Manager',
        description:
          'Managers har full administrativ kontroll för att övervaka flottan, övervaka enheternas hälsa och hantera alla enheter, inställningar och användaråtkomst.',
      },
      technician: {
        title: 'Technician',
        description:
          'Fokuserar på enhetens hälsa och underhåll. Tekniker kan övervaka enhetsdata och utföra fjärrprogramuppdateringar. Denna roll inkluderar inte behörigheter för flott- eller användarhantering.',
      },
      viewer: {
        title: 'Viewer',
        description:
          'Betraktare har skrivskyddad åtkomst. De kan övervaka enhetsstatus och visa rapporter, men kan inte ändra inställningar eller konfigurationer.',
      },
    },
    campaign_types: {
      title: 'Förstå kampanjtyper',
      recall: {
        title: 'Återkallelse',
        description:
          'Retroaktivt införande av produktionsändring; bör genomföras så snart som möjligt för att säkerställa korrekt funktion hos produkten.',
      },
      quality: {
        title: 'Kvalitet',
        description:
          'Retroaktivt införande av produktionsändring; bör genomföras vid nästa schemalagda service för att säkerställa korrekt funktion hos produkten.',
      },
      service: {
        title: 'Service',
        description:
          'Säkerhetsrelaterat retroaktivt införande av produktionsändring; måste genomföras omedelbart för att säkerställa säker drift av produkten.',
      },
      installation: {
        title: 'Installation',
        description:
          'En fjärrprogramvaruuppdatering är redo att installeras på din enhet med hjälp av Volvo Penta-appen.',
        app_store: 'Hämta den för iOS i App Store',
        google_play: 'Hämta den för Android på Google Play',
      },
    },
  },
};

export default svTranslation;
